import { SlateElement } from '@all-hands-hq/plex-graphql-schema';
import { CircularProgress, makeStyles, TableCell, Typography } from '@material-ui/core';
import grey from '@material-ui/core/colors/grey';
import assertNever from 'assert-never';
import classNames from 'classnames';
import { ClassValue } from 'classnames/types';
import React from 'react';
import { useFocused, useSelected } from 'slate-react';

import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { AnswerLinkPreview } from '../../../logged_in/feed/QuestionView/AnswerLinkPreview';
import { PlexTypography } from '../../primitives/PlexTypography';
import { getEntity, useEntitiesContext } from '../entities';
import { TableElement } from './TableElement';
import { TableRowElement } from './TableRowElement';

export function SlateElementView({
  editable,
  hideImages,
  attributes,
  children,
  element,
}: {
  editable?: boolean;
  hideImages?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: { [key: string]: any };
  children?: React.ReactNode;
  element: SlateElement;
}): React.ReactElement | null {
  const entities = useEntitiesContext();
  const selected = useSelected();
  const focused = useFocused();
  const classes = useStyles();
  const withEditable = (...className: ClassValue[]): string =>
    editable ? classNames(classes.editable, ...className) : classNames(className);
  switch (element.type) {
    case 'block-quote':
      return (
        <Typography variant="body1" component="div" className={withEditable(classes.quote)} {...attributes}>
          {children}
        </Typography>
      );
    case 'bulleted-list':
      return <ul {...attributes}>{children}</ul>;

    case 'expanded_link': {
      const { url, linkText } = element;

      const link = getEntity(entities, element);

      return (
        <div {...attributes}>
          <div contentEditable={false} className={withEditable(selected && focused && classes.focused)}>
            <AnswerLinkPreview href={url} linkText={linkText} data={{ state: 'done', details: link.details }} />
          </div>
          {children}
        </div>
      );
    }
    case 'list-item':
      return <li {...attributes}>{children}</li>;
    case 'numbered-list':
      return (
        <ol start={element.startNumber} {...attributes}>
          {children}
        </ol>
      );
    case 'link':
      return (
        <a {...attributes} href={element.url} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    case 'image':
      return !hideImages ? (
        <div {...attributes}>
          <div
            contentEditable={false}
            className={withEditable(classes.centeredContainer, selected && focused && classes.focused)}
          >
            <img src={element.url} className={classes.image} alt="" />
          </div>
          {children}
        </div>
      ) : null;
    case 'upload_in_progress': {
      return (
        <div {...attributes}>
          <div
            contentEditable={false}
            className={withEditable(classes.centeredContainer, selected && focused && classes.focused)}
          >
            <PlexTypography className={classes.placeholderMessage} size={TextSizesEnum.Normal}>
              {element.message}
            </PlexTypography>
            <CircularProgress size={16} className={classes.placeholderCircularProgress} />
          </div>
          {children}
        </div>
      );
    }
    case 'paragraph':
      const namedStyleType = element.namedStyleType || 'NORMAL_TEXT';

      return (
        <Typography
          variant="body1"
          style={paragraphSizes[namedStyleType]}
          className={withEditable('')}
          component="p"
          {...attributes}
        >
          {children}
        </Typography>
      );
    case 'mention':
      const { contentEditable, ...otherAttributes } = attributes;
      const mention = getEntity(entities, element);

      return (
        <span
          {...otherAttributes}
          style={{
            padding: '3px 3px 2px',
            margin: '0 1px',
            verticalAlign: 'baseline',
            display: 'inline-block',
            borderRadius: '4px',
            backgroundColor: '#EAF5FB',
            color: '#1364A3',
            fontSize: '0.9em',
            boxShadow: selected && focused ? '0 0 0 2px #B4D5FF' : 'none',
          }}
        >
          {/*need this additional span for deletes https://github.com/ianstormtaylor/slate/issues/3813#issuecomment-668634790*/}
          <span contentEditable={contentEditable}>@{mention.name}</span>
          {children}
        </span>
      );
    case 'table': {
      return <TableElement>{children}</TableElement>;
    }
    case 'table-row': {
      return <TableRowElement>{children}</TableRowElement>;
    }
    case 'table-cell': {
      return (
        <TableCell colSpan={element.colSpan} rowSpan={element.rowSpan} className={classes.tableCell}>
          {children}
        </TableCell>
      );
    }
    default:
      try {
        assertNever(element);
      } catch (e) {
        // Deprecated element types can fall in here
        // Bubble the error to the console, but do not interfere with the current stack
        console.error(e);
      }
      return null;
  }
}

const useStyles = makeStyles((theme) => ({
  editable: {
    pointerEvents: 'none',
  },
  heading: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  quote: {
    paddingLeft: theme.spacing(1),
    marginLeft: theme.spacing(2),
    borderLeftWidth: '2px',
    borderLeftColor: grey[400],
    borderLeftStyle: 'solid',
    color: grey[700],
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  centeredContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    padding: spacings.xsmall,
  },
  image: {
    maxWidth: '100%',
    maxHeight: '20em',
  },
  focused: {
    boxShadow: '0 0 0 3px #B4D5FF',
  },
  placeholderMessage: {
    color: colorPalette.gray._400,
    margin: spacings.normal,
    display: 'inline-block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  placeholderCircularProgress: {
    flexShrink: 0,
    color: colorPalette.gray._400,
    marginRight: theme.spacing(1),
  },
  tableCell: {
    verticalAlign: 'top',
  },
}));

const paragraphSizes = {
  NORMAL_TEXT: {
    fontSize: '1rem',
    lineHeight: '1.2',
  },
  HEADING_6: {
    fontSize: '1.1rem',
    lineHeight: '1.2',
    color: '#424242',
    marginTop: '3px',
    marginBottom: '3px',
  },
  HEADING_5: {
    fontSize: '1.2rem',
    lineHeight: '1.2',
    marginTop: '4px',
    marginBottom: '4px',
  },
  HEADING_4: {
    fontSize: '1.3rem',
    lineHeight: '1.2',
    marginTop: '5px',
    marginBottom: '5px',
  },
  HEADING_3: {
    fontSize: '1.4rem',
    lineHeight: '1.2',
    fontWeight: 400,
    marginTop: '6px',
    marginBottom: '6px',
  },
  HEADING_2: {
    fontSize: '1.5rem',
    lineHeight: '1.2',
    fontWeight: 420,
    marginTop: '8px',
    marginBottom: '8px',
  },
  HEADING_1: {
    fontSize: '1.7rem',
    lineHeight: '1.2',
    fontWeight: 450,
    marginTop: '10px',
    marginBottom: '10px',
  },
  SUBTITLE: {
    fontSize: '1rem',
    lineHeight: '1.1',
    color: '#424242',
    marginBottom: '3px',
  },
  TITLE: {
    fontSize: '2.2rem',
    lineHeight: '1.2',
    fontWeight: 480,
    marginTop: '14px',
    marginBottom: '14px',
  },
};
